import {Fragment, useEffect, useMemo, useState, useCallback} from 'react'
import {
  getVirtualNumbers,
  getVirtualNumberCounts,
  syncVirtualNumbers,
  assignVirtualNumber,
  unassignVirtualNumber,
  searchAccounts,
  getVirtualNumberGroups,
  getAccountLeads,
  updateLead,
  addAccountNote,
  VirtualNumber,
  VirtualNumberCounts,
  AccountSearchResult,
  VirtualNumberGroup,
  WhatsappLead,
} from './_requests'
import {SignatureSetupModal, SetupAccount} from './SignatureManager'
import {loginAsUser} from '../payments/_requests'

// The actions column carries שייך / ערוך / כניסה / בטל שיוך. The table lives in a
// .table-responsive (overflow-x: auto) and grew past the viewport when the activity
// and "advertising since" columns were added, which left those buttons scrolled off
// screen. Pinning the column keeps them reachable however wide the table gets.
// The page is RTL, so the last cell renders at the LEFT edge — hence `left: 0`.
const STICKY_ACTIONS_CELL: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  background: '#fff',
  boxShadow: '2px 0 6px -4px rgba(0,0,0,0.25)',
}
const STICKY_ACTIONS_HEAD: React.CSSProperties = {
  ...STICKY_ACTIONS_CELL,
  zIndex: 3,
}

const PROFILE_STATUS_LABEL: Record<string, string> = {
  connected: 'מחובר',
  disconnected: 'מנותק',
  error: 'שגיאת התחברות',
}

// The activity block mirrors the admin payments card, minus the two figures that
// already have their own columns here (profiles and posted-14d).
const ACTIVITY_ITEMS: Array<{key: string; label: string; title: string; bad?: boolean}> = [
  {key: 'campaign_count', label: 'קמפ׳', title: 'קמפיינים בחשבון'},
  {key: 'post_count', label: 'פוסט', title: 'פוסטים שהוגדרו בקמפיינים'},
  {key: 'group_count', label: 'קבוצ׳', title: 'קבוצות משויכות'},
  {key: 'scheduled_today', label: 'היום', title: 'סלוטים מתוזמנים להיום'},
  {key: 'posted_today', label: 'פורסם', title: 'פורסמו היום'},
  {key: 'missed_today', label: 'פספוס', title: 'סלוטים שהשעה שלהם עברה ולא פורסמו', bad: true},
  {key: 'errors_today', label: 'שגיאות', title: 'סלוטים שנכשלו היום', bad: true},
]

// "לפני 3 חודשים" — how long the number has been out there advertising.
function daysSince(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (days <= 0) return 'היום'
  if (days === 1) return 'אתמול'
  if (days < 30) return `לפני ${days} ימים`
  const months = Math.floor(days / 30)
  if (months < 12) return `לפני ${months} ${months === 1 ? 'חודש' : 'חודשים'}`
  const years = Math.floor(days / 365)
  return `לפני ${years} ${years === 1 ? 'שנה' : 'שנים'}`
}

function fmtLeadDate(iso: string) {
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleString('he-IL', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'})
  } catch {
    return iso
  }
}

// Admin view of one customer's WhatsApp leads — same list the client sees on their
// own virtual-number page. Opened by clicking a customer name in the table.
function LeadsModal({
  account,
  onClose,
}: {
  account: {_id: string; label: string}
  onClose: () => void
}) {
  const [leads, setLeads] = useState<WhatsappLead[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        const res = await getAccountLeads(account._id)
        setLeads(res.data.leads || [])
      } catch (e: any) {
        setError(e?.response?.data?.error || e.message || 'שגיאה בטעינת הלידים')
      } finally {
        setLoading(false)
      }
    })()
  }, [account._id])

  const patch = async (id: string, body: {status?: string; notes?: string}) => {
    try {
      const res = await updateLead(id, body)
      setLeads((prev) =>
        prev.map((l) => (l.id === id ? {...l, status: res.data.status, notes: res.data.notes} : l))
      )
    } catch {
      /* leave UI as-is */
    }
  }

  return (
    <div
      onClick={onClose}
      style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1090, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, overflowY: 'auto'}}
    >
      <div onClick={(e) => e.stopPropagation()} className='card' dir='rtl' style={{maxWidth: 940, width: '100%', margin: 'auto'}}>
        <div className='card-header align-items-center'>
          <div>
            <h3 className='card-title mb-0'>לידים מוואטסאפ — {account.label}</h3>
            {!loading && <div className='text-muted fs-8 mt-1'>{leads.length} לידים</div>}
          </div>
          <button className='btn btn-sm btn-icon btn-light' onClick={onClose}>✕</button>
        </div>
        <div className='card-body p-0'>
          {loading ? (
            <div className='text-muted py-10 text-center'>טוען…</div>
          ) : error ? (
            <div className='alert alert-danger m-4'>{error}</div>
          ) : leads.length === 0 ? (
            <div className='text-muted py-10 text-center'>אין לידים עדיין.</div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-bordered align-middle gy-3 mb-0'>
                <thead>
                  <tr className='fw-bold text-muted fs-7 text-nowrap'>
                    <th className='ps-6'>תאריך</th>
                    <th>שם</th>
                    <th>טלפון</th>
                    <th>נכס</th>
                    <th>הודעה</th>
                    <th>סטטוס</th>
                    <th>מעקב</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => {
                    const handled = l.status === 'handled'
                    return (
                      <tr key={l.id}>
                        <td className='ps-6 text-nowrap text-muted fs-7'>{fmtLeadDate(l.created_at)}</td>
                        <td className='text-nowrap'>{l.lead_name || '—'}</td>
                        <td className='text-nowrap' dir='ltr'>
                          <a href={`https://wa.me/${l.lead_phone.replace(/\D/g, '')}`} target='_blank' rel='noreferrer'>
                            {l.lead_phone}
                          </a>
                        </td>
                        <td style={{minWidth: 180, maxWidth: 260}}>
                          {l.matched ? (
                            <a className='d-flex align-items-center gap-2 text-reset text-decoration-none' href={`https://l.fbzipper.com/${l.listing_code}`} target='_blank' rel='noreferrer' title='פתח קישור נכס'>
                              {l.listing_image && (
                                <img src={l.listing_image} alt='' style={{width: 40, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0}} />
                              )}
                              <div style={{minWidth: 0}}>
                                <span className='badge badge-light-success mb-1' dir='ltr'>{l.listing_code}</span>
                                {l.listing_deleted && <span className='badge badge-light-danger mb-1 ms-1'>נמחק</span>}
                                {l.listing_text && (
                                  <div className='text-muted fs-8' style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} title={l.listing_text}>
                                    {l.listing_text}
                                    {l.listing_price ? ` · ₪${l.listing_price}` : ''}
                                  </div>
                                )}
                              </div>
                            </a>
                          ) : (
                            <span className='badge badge-light-warning'>לא זוהה</span>
                          )}
                        </td>
                        <td className='text-muted' style={{maxWidth: 220, whiteSpace: 'normal'}}>{l.message}</td>
                        <td>
                          <button
                            className={`btn btn-sm ${handled ? 'btn-success' : 'btn-light-danger'}`}
                            onClick={() => patch(l.id, {status: handled ? 'new' : 'handled'})}
                          >
                            {handled ? 'טופל ✓' : 'לא טופל'}
                          </button>
                        </td>
                        <td style={{minWidth: 180}}>
                          <input
                            className='form-control form-control-sm'
                            placeholder='מעקב / הערה…'
                            defaultValue={l.notes || ''}
                            onBlur={(e) => { if (e.target.value !== (l.notes || '')) patch(l.id, {notes: e.target.value}) }}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function accountLabel(a?: VirtualNumber['account']): string {
  if (!a) return ''
  const name = [a.first_name, a.last_name].filter(Boolean).join(' ').trim()
  return name || a.company_name || a.email || a.username || String(a._id)
}

// Redirect numbers are always stored in Israeli local format (05xxxxxxxx), never
// 972. Account phones are often international, so normalize on autofill/save:
//   972521234567 / +972-52-1234567 -> 0521234567 ;  521234567 -> 0521234567
function toLocalIsraeliPhone(raw?: string): string {
  let d = String(raw || '').replace(/\D/g, '')
  if (d.startsWith('972')) d = '0' + d.slice(3)
  else if (d.length && d[0] !== '0') d = '0' + d
  return d
}
const IL_LOCAL_RE = /^0\d{8,9}$/

export default function VirtualNumbersPage() {
  const [numbers, setNumbers] = useState<VirtualNumber[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState('')

  // signature/settings popup (opened per customer from a table row)
  const [setupAccount, setSetupAccount] = useState<SetupAccount | null>(null)

  // leads popup (opened by clicking a customer name)
  const [leadsAccount, setLeadsAccount] = useState<{_id: string; label: string} | null>(null)

  // assign modal
  const [assignDdi, setAssignDdi] = useState<string | null>(null)
  const [accQuery, setAccQuery] = useState('')
  const [accResults, setAccResults] = useState<AccountSearchResult[]>([])
  const [selectedAcc, setSelectedAcc] = useState<AccountSearchResult | null>(null)
  const [forwardNumber, setForwardNumber] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [saving, setSaving] = useState(false)

  // PayCall groups (for the live redirect push)
  const [groups, setGroups] = useState<VirtualNumberGroup[]>([])
  const [pushEnabled, setPushEnabled] = useState(false)
  const [groupId, setGroupId] = useState('')

  // free-text search over the table (number, redirect number, customer, note)
  const [query, setQuery] = useState('')

  // per-number call + WhatsApp counts (date-range filtered)
  const [counts, setCounts] = useState<VirtualNumberCounts>({})
  const [countsLoading, setCountsLoading] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [activeRange, setActiveRange] = useState<'day' | 'week' | 'month' | 'all' | ''>('')

  const loadCounts = useCallback(async (f?: string, t?: string) => {
    setCountsLoading(true)
    try {
      const res = await getVirtualNumberCounts(f, t)
      setCounts(res.data.counts || {})
      if (res.data.period && (!f || !t)) {
        setFrom(res.data.period.from)
        setTo(res.data.period.to)
      }
    } catch {
      setCounts({})
    } finally {
      setCountsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadCounts()
  }, [loadCounts])

  // Quick stat ranges: today / last 7 days / last 30 days / all time.
  const fmtDate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const applyRange = (preset: 'day' | 'week' | 'month' | 'all') => {
    const today = new Date()
    const t = fmtDate(today)
    let f = t
    if (preset === 'week') { const d = new Date(); d.setDate(d.getDate() - 6); f = fmtDate(d) }
    else if (preset === 'month') { const d = new Date(); d.setDate(d.getDate() - 29); f = fmtDate(d) }
    else if (preset === 'all') { f = '2020-01-01' }
    setFrom(f)
    setTo(t)
    setActiveRange(preset)
    loadCounts(f, t)
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await getVirtualNumbers()
      setNumbers(res.data.numbers || [])
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'שגיאה בטעינה')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // load PayCall groups once (for the redirect-push group picker)
  useEffect(() => {
    ;(async () => {
      try {
        const res = await getVirtualNumberGroups()
        setGroups(res.data.groups || [])
        setPushEnabled(!!res.data.push_enabled)
      } catch {
        setGroups([])
      }
    })()
  }, [])

  const onSync = async () => {
    setSyncing(true)
    setError('')
    try {
      const res = await syncVirtualNumbers()
      setNumbers(res.data.numbers || [])
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'שגיאת סנכרון')
    } finally {
      setSyncing(false)
    }
  }

  // debounced account search
  useEffect(() => {
    if (assignDdi === null) return
    const q = accQuery.trim()
    if (q.length < 2) {
      setAccResults([])
      return
    }
    const t = setTimeout(async () => {
      try {
        const res = await searchAccounts(q)
        setAccResults(res.data.accounts || [])
      } catch {
        setAccResults([])
      }
    }, 350)
    return () => clearTimeout(t)
  }, [accQuery, assignDdi])

  const openAssign = (vn: VirtualNumber) => {
    setAssignDdi(vn.ddi)
    setSelectedAcc(vn.account ? {_id: vn.account._id, first_name: vn.account.first_name, last_name: vn.account.last_name, company_name: vn.account.company_name, email: vn.account.email, phone: vn.account.phone} : null)
    // Default the forward/redirect number to the assigned account's own phone (local 05x format).
    setForwardNumber(toLocalIsraeliPhone(vn.forward_number || vn.account?.phone || ''))
    setEnabled(vn.enabled !== false)
    setAccQuery('')
    setAccResults([])
  }

  const closeAssign = () => {
    setAssignDdi(null)
    setSelectedAcc(null)
    setAccResults([])
  }

  const onAssign = async () => {
    if (!assignDdi || !selectedAcc) return
    // Always store the redirect in local 05x format — never 972.
    const localForward = toLocalIsraeliPhone(forwardNumber)
    if (localForward && !IL_LOCAL_RE.test(localForward)) {
      setError('מספר הפניה לא תקין — יש להזין בפורמט 05xxxxxxxx (לא 972)')
      return
    }
    if (localForward !== forwardNumber) setForwardNumber(localForward)
    if (pushEnabled && localForward && !groupId) {
      setError('יש לבחור קבוצה כדי לשייך את ההפניה ב-PayCall')
      return
    }
    setSaving(true)
    setError('')
    try {
      await assignVirtualNumber(assignDdi, {
        account_id: selectedAcc._id,
        forward_number: localForward,
        enabled,
        reassign: true,
        group_id: groupId || undefined,
      })
      closeAssign()
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'שגיאת שיוך')
    } finally {
      setSaving(false)
    }
  }

  // The note here is the CUSTOMER's pinned note — the same one the campaigns and
  // payments pages show — not a note that lives only on this page. Notes accumulate
  // (each save adds one), so the newest is what gets displayed.
  const latestAccountNote = (vn: VirtualNumber) => {
    const notes = vn.account?.important_notes || []
    return notes.length ? notes[notes.length - 1] : null
  }

  const onEditNote = async (vn: VirtualNumber) => {
    if (!vn.account?._id) return
    const current = latestAccountNote(vn)
    const next = window.prompt(
      `הערה עבור ${accountLabel(vn.account)} (מוצגת גם בעמוד הלקוח ובתשלומים):`,
      current?.text || ''
    )
    if (next === null) return // cancelled — leave the existing note alone
    if (!next.trim()) return
    try {
      const res = await addAccountNote(vn.account._id, next.trim())
      const updated = res.data?.admin_notes || []
      // Every number belonging to this account shows the same note, so update them all.
      setNumbers((prev) => prev.map((n) => (n.account?._id === vn.account?._id
        ? {...n, account: {...n.account!, important_notes: updated}}
        : n)))
    } catch (e: any) {
      alert(e?.response?.data?.error || 'שמירת ההערה נכשלה')
    }
  }

  // Open the customer's account in a new tab, same handoff the payments page uses:
  // mint a token, drop it into the new window's localStorage, land on /campaigns.
  const [loginAsBusy, setLoginAsBusy] = useState<string | null>(null)
  const onLoginAs = async (vn: VirtualNumber) => {
    if (!vn.account?._id) return
    setLoginAsBusy(vn.ddi)
    try {
      const res = await loginAsUser(vn.account._id)
      if (res.data?.success && res.data.api_token) {
        const w = window.open('about:blank', '_blank')
        if (w) {
          w.localStorage.setItem('kt-auth-react-v', JSON.stringify({api_token: res.data.api_token}))
          w.location.href = '/campaigns'
        }
      } else {
        alert(res.data?.error || 'שגיאה בכניסה כמשתמש')
      }
    } catch (e: any) {
      alert(e?.response?.data?.error || 'שגיאה בכניסה כמשתמש')
    }
    setLoginAsBusy(null)
  }

  const onUnassign = async (ddi: string) => {
    if (!window.confirm(`לבטל את השיוך של המספר ${ddi}?\n\nהמספר ייכנס להקפאה של שבועיים ולא ניתן יהיה לשייך אותו ללקוח אחר עד אז.`)) return
    setError('')
    try {
      await unassignVirtualNumber(ddi)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'שגיאה בביטול שיוך')
    }
  }

  const accLabel = (a: AccountSearchResult) =>
    [a.first_name, a.last_name].filter(Boolean).join(' ').trim() || a.company_name || a.email || a._id

  // Free-text search. Phone digits are compared without separators or a 972
  // prefix, so 054-123 / 972541234567 / 0541234567 all find the same number;
  // everything else is a plain case-insensitive substring match.
  const filtered = useMemo<VirtualNumber[]>(() => {
    const q = query.trim().toLowerCase()
    if (!q) return numbers
    const qDigits = toLocalIsraeliPhone(q)
    const digitsOnly = /^[\d\-+ ]+$/.test(query.trim())
    return numbers.filter((vn) => {
      if (digitsOnly && qDigits.length > 1) {
        if (toLocalIsraeliPhone(vn.ddi).includes(qDigits)) return true
        if (toLocalIsraeliPhone(vn.forward_number).includes(qDigits)) return true
        if (toLocalIsraeliPhone(vn.account?.phone).includes(qDigits)) return true
      }
      const hay = [
        vn.ddi,
        vn.forward_number,
        accountLabel(vn.account),
        vn.account?.company_name,
        vn.account?.email,
        vn.account?.username,
        vn.account?.phone,
        ...(vn.account?.important_notes || []).map((n) => n.text),
      ]
      return hay.some((v) => String(v || '').toLowerCase().includes(q))
    })
  }, [numbers, query])

  // Group numbers by customer; free (unassigned) numbers go to their own bucket.
  type CustGroup = {account: NonNullable<VirtualNumber['account']>; nums: VirtualNumber[]}
  const numGroups = useMemo<{customers: CustGroup[]; free: VirtualNumber[]}>(() => {
    const byAcc = new Map<string, CustGroup>()
    const free: VirtualNumber[] = []
    for (const vn of filtered) {
      if (vn.account) {
        const k = vn.account._id
        if (!byAcc.has(k)) byAcc.set(k, {account: vn.account, nums: []})
        byAcc.get(k)!.nums.push(vn)
      } else free.push(vn)
    }
    return {customers: Array.from(byAcc.values()), free}
  }, [filtered])

  // Multi-assign free numbers to a customer (each profile can then use its own).
  const [selectedFree, setSelectedFree] = useState<Set<string>>(new Set())
  const [batchTarget, setBatchTarget] = useState('')
  const [batchBusy, setBatchBusy] = useState(false)
  const toggleFree = (ddi: string) =>
    setSelectedFree((prev) => {
      const n = new Set(prev)
      if (n.has(ddi)) n.delete(ddi)
      else n.add(ddi)
      return n
    })
  const assignSelected = async () => {
    if (!batchTarget || selectedFree.size === 0) return
    setBatchBusy(true)
    setError('')
    try {
      for (const ddi of Array.from(selectedFree)) {
        await assignVirtualNumber(ddi, {account_id: batchTarget, enabled: true})
      }
      setSelectedFree(new Set())
      setBatchTarget('')
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'שגיאה בשיוך')
    } finally {
      setBatchBusy(false)
    }
  }

  const numberRow = (vn: VirtualNumber, free?: boolean) => (
    <tr key={vn._id}>
      <td className='fw-bold'>
        {free ? (
          <label className='d-flex align-items-center gap-2 m-0' style={{cursor: 'pointer'}}>
            <input type='checkbox' checked={selectedFree.has(vn.ddi)} onChange={() => toggleFree(vn.ddi)} style={{width: 16, height: 16, margin: 0}} />
            <span dir='ltr'>{vn.ddi}</span>
          </label>
        ) : (
          <span dir='ltr'>{vn.ddi}</span>
        )}
      </td>
      <td>{vn.account ? <span className='badge badge-light-success'>משויך</span> : <span className='badge badge-light'>פנוי</span>}</td>
      <td dir='ltr' className='text-end'>{vn.forward_number || '—'}</td>
      <td>{vn.account ? (vn.enabled ? <span className='badge badge-light-primary'>פעיל</span> : <span className='badge badge-light-warning'>כבוי</span>) : '—'}</td>
      <td className='text-center fw-bold'>{vn.account ? (counts[vn.ddi]?.phone_calls ?? (countsLoading ? '…' : 0)) : '—'}</td>
      <td className='text-center fw-bold text-primary'>{vn.account ? (counts[vn.ddi]?.whatsapp_leads ?? (countsLoading ? '…' : 0)) : '—'}</td>
      <td className='text-center fw-bold text-info'>{vn.account ? (counts[vn.ddi]?.marketplace_inquiries ?? (countsLoading ? '…' : 0)) : '—'}</td>
      <td className='text-center fw-bold text-warning'>{vn.account ? (counts[vn.ddi]?.post_comments ?? (countsLoading ? '…' : 0)) : '—'}</td>
      {/* Is the account actually running? A number on a dormant account is reclaimable. */}
      <td className='text-center fw-bold'>
        {vn.account ? (
          <span
            title={(vn.profiles || []).length
              ? (vn.profiles || []).map((p) => `${p.first_name || ''} ${p.last_name || ''} — ${PROFILE_STATUS_LABEL[p.status]}`).join('\n')
              : 'אין פרופילים בחשבון'}
          >
            <span className={(vn.profiles_connected ?? 0) > 0 ? 'text-success' : 'text-danger'}>
              {vn.profiles_connected ?? 0}
            </span>
            <span className='text-muted fw-normal'>/{vn.account_stats?.profile_count ?? 0}</span>
          </span>
        ) : '—'}
      </td>
      <td className={`text-center fw-bold ${(vn.posted_count_14d ?? 0) > 0 ? 'text-success' : 'text-danger'}`}>
        {vn.account ? (vn.posted_count_14d ?? 0) : '—'}
      </td>
      {/* The same activity block the payments card shows for this customer. */}
      <td className='text-center'>
        {vn.account && vn.account_stats ? (
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, auto)', gap: '1px 8px', justifyContent: 'center', fontSize: 10, maxWidth: 118}}>
            {ACTIVITY_ITEMS.map((it) => {
              const value = (vn.account_stats as any)[it.key] ?? 0
              return (
                <span key={it.key} className='text-nowrap' title={it.title}>
                  <span className={`fw-bold ${it.bad ? (value > 0 ? 'text-danger' : 'text-muted') : (value > 0 ? 'text-dark' : 'text-muted')}`}>{value}</span>
                  <span className='text-muted ms-1' style={{fontSize: 9}}>{it.label}</span>
                </span>
              )
            })}
          </div>
        ) : '—'}
      </td>
      <td className='text-center fs-8 text-nowrap'>
        {!vn.account ? '—' : vn.first_advertised_at ? (
          <span title={`המספר שובץ לראשונה בפוסט ב-${new Date(vn.first_advertised_at).toLocaleDateString('he-IL')}${vn.assigned_at ? `\nהוקצה ללקוח ב-${new Date(vn.assigned_at).toLocaleDateString('he-IL')}` : ''}`}>
            <span className='fw-bold'>{new Date(vn.first_advertised_at).toLocaleDateString('he-IL')}</span>
            <br />
            <span className='text-muted' style={{fontSize: 10}}>
              {daysSince(vn.first_advertised_at)} · {vn.advertised_posts ?? 0} פוסטים
            </span>
          </span>
        ) : (
          <span className='text-danger' title={vn.assigned_at ? `הוקצה ב-${new Date(vn.assigned_at).toLocaleDateString('he-IL')} אך עוד לא יצא באף פוסט` : 'המספר עוד לא יצא באף פוסט'}>
            טרם פורסם
          </span>
        )}
      </td>
      <td style={{minWidth: 150, maxWidth: 240}}>
        {vn.account ? (() => {
          const note = latestAccountNote(vn)
          return note ? (
            <div
              role='button'
              title={`${note.text}\n(לחצו להוספת הערה חדשה)`}
              onClick={() => onEditNote(vn)}
              style={{
                background: '#fff8e1', border: '1px solid #ffc107', borderRadius: 8,
                padding: '8px 12px', direction: 'rtl', fontSize: 13,
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              }}
            >
              <span style={{fontWeight: 700}}>📌</span>
              <span style={{flex: 1}}>{note.text}</span>
              <span style={{color: '#999', fontSize: 10, whiteSpace: 'nowrap'}}>
                {note.created_at ? new Date(note.created_at).toLocaleDateString('he-IL') : ''}
              </span>
            </div>
          ) : (
            <span
              role='button'
              className='text-muted fs-8'
              title='הוסף הערה ללקוח'
              style={{whiteSpace: 'normal', cursor: 'pointer', display: 'block'}}
              onClick={() => onEditNote(vn)}
            >
              ＋ הערה
            </span>
          )
        })() : '—'}
      </td>
      <td className='text-end text-nowrap' style={STICKY_ACTIONS_CELL}>
        {/* A number freed in the last two weeks still rings for the previous owner's
            callers, so it isn't offered for assignment until the quarantine is over. */}
        {!vn.account && vn.quarantined_until ? (
          <span className='badge badge-light-warning me-2' title='המספר שוחרר לאחרונה ונמצא בהקפאה'>
            בהקפאה עד {new Date(vn.quarantined_until).toLocaleDateString('he-IL')}
          </span>
        ) : (
          <button className='btn btn-sm btn-light-primary me-2' onClick={() => openAssign(vn)}>{vn.account ? 'ערוך' : 'שייך'}</button>
        )}
        {vn.account && (
          <button
            className='btn btn-sm btn-light-info me-2'
            disabled={loginAsBusy === vn.ddi}
            title='כניסה לחשבון הלקוח'
            onClick={() => onLoginAs(vn)}
          >
            {loginAsBusy === vn.ddi ? '…' : 'כניסה'}
          </button>
        )}
        {vn.account && (
          <button className='btn btn-sm btn-light-danger' onClick={() => onUnassign(vn.ddi)}>בטל שיוך</button>
        )}
      </td>
    </tr>
  )

  return (
    <div className='p-6' dir='rtl' style={{maxWidth: 1100, margin: '0 auto'}}>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <h2 className='m-0'>מספרים וירטואליים</h2>
        <button className='btn btn-primary' onClick={onSync} disabled={syncing}>
          {syncing ? 'מסנכרן…' : 'סנכרן מ-PayCall'}
        </button>
      </div>

      {error && <div className='alert alert-danger'>{error}</div>}

      <div className='d-flex gap-3 align-items-end mb-4 flex-wrap'>
        <div style={{minWidth: 260}}>
          <label className='form-label fs-8 text-muted mb-1'>חיפוש</label>
          <div className='position-relative'>
            <input
              type='search'
              className='form-control form-control-sm'
              placeholder='מספר, מספר הפניה, לקוח, אימייל או הערה'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                type='button'
                className='btn btn-sm btn-icon position-absolute top-50 translate-middle-y'
                style={{left: 2, width: 24, height: 24}}
                title='נקה חיפוש'
                onClick={() => setQuery('')}
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <div>
          <label className='form-label fs-8 text-muted mb-1'>טווח מהיר</label>
          <div className='btn-group btn-group-sm' role='group'>
            {([
              {key: 'day', label: 'יום'},
              {key: 'week', label: 'שבוע'},
              {key: 'month', label: 'חודש'},
              {key: 'all', label: 'כל הזמנים'},
            ] as const).map((r) => (
              <button
                key={r.key}
                type='button'
                className={`btn btn-sm ${activeRange === r.key ? 'btn-primary' : 'btn-light-primary'}`}
                onClick={() => applyRange(r.key)}
                disabled={countsLoading}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className='form-label fs-8 text-muted mb-1'>מתאריך</label>
          <input type='date' className='form-control form-control-sm' value={from} onChange={(e) => { setFrom(e.target.value); setActiveRange('') }} />
        </div>
        <div>
          <label className='form-label fs-8 text-muted mb-1'>עד תאריך</label>
          <input type='date' className='form-control form-control-sm' value={to} onChange={(e) => { setTo(e.target.value); setActiveRange('') }} />
        </div>
        <button className='btn btn-sm btn-light-primary' onClick={() => loadCounts(from, to)} disabled={countsLoading}>
          {countsLoading ? 'טוען…' : 'עדכן ספירה'}
        </button>
        {(() => {
          // Totals follow the search: filter to one customer and the badges show
          // that customer's traffic, not the whole fleet's.
          const vals = filtered.map((vn) => counts[vn.ddi]).filter(Boolean) as {
            phone_calls?: number
            whatsapp_leads?: number
            marketplace_inquiries?: number
            post_comments?: number
          }[]
          const totalCalls = vals.reduce((s, c) => s + (c.phone_calls || 0), 0)
          const totalLeads = vals.reduce((s, c) => s + (c.whatsapp_leads || 0), 0)
          const totalMp = vals.reduce((s, c) => s + (c.marketplace_inquiries || 0), 0)
          const totalComments = vals.reduce((s, c) => s + (c.post_comments || 0), 0)
          return (
            <div className='d-flex gap-2 ms-auto align-items-center flex-wrap'>
              {query.trim() && (
                <span className='badge badge-light-dark fs-7 px-3 py-2'>
                  מציג {filtered.length} מתוך {numbers.length}
                </span>
              )}
              <span className='badge badge-light-primary fs-7 px-3 py-2'>סה״כ שיחות: {totalCalls}</span>
              <span className='badge badge-light-success fs-7 px-3 py-2'>סה״כ לידים: {totalLeads}</span>
              <span className='badge badge-light-info fs-7 px-3 py-2'>סה״כ מרקטפלייס: {totalMp}</span>
              <span className='badge badge-light-warning fs-7 px-3 py-2'>סה״כ תגובות: {totalComments}</span>
            </div>
          )
        })()}
      </div>

      {loading ? (
        <div className='text-muted'>טוען…</div>
      ) : (
        <div className='table-responsive'>
          <table className='table table-row-bordered table-hover align-middle'>
            <thead>
              <tr className='fw-bold text-muted'>
                <th>מספר</th>
                <th>סטטוס</th>
                <th>מספר הפניה</th>
                <th>מעקב</th>
                <th className='text-center'>שיחות</th>
                <th className='text-center'>וואטסאפ</th>
                <th className='text-center' title='פניות שהתקבלו במרקטפלייס בטווח התאריכים'>מרקטפלייס</th>
                <th className='text-center' title='תגובות שהתקבלו על הפוסטים בקבוצות בטווח התאריכים'>תגובות בפוסטים</th>
                <th className='text-center' title='פרופילי פייסבוק מחוברים כרגע / סה״כ פרופילים בחשבון'>פרופילים</th>
                <th className='text-center' title='פוסטים שפורסמו ב-14 הימים האחרונים (כולל קרוס-פוסט)'>פורסמו 14י׳</th>
                <th className='text-center' title='פעילות החשבון: קמפיינים · פוסטים · קבוצות · מתוזמן/פורסם היום · שגיאות'>פעילות</th>
                <th className='text-center' title='מתי המספר יצא לראשונה בפוסט, ובכמה פוסטים הוא משובץ'>מפרסם מאז</th>
                <th>הערה</th>
                <th style={STICKY_ACTIONS_HEAD}></th>
              </tr>
            </thead>
            <tbody>
              {numGroups.customers.map((g: CustGroup) => (
                <Fragment key={g.account._id}>
                  <tr className='table-active'>
                    <td colSpan={10} className='fw-bold'>
                      👤{' '}
                      <span
                        className='text-primary'
                        style={{cursor: 'pointer', textDecoration: 'underline'}}
                        title='הצג לידים מוואטסאפ'
                        onClick={() => setLeadsAccount({_id: g.account._id, label: accountLabel(g.account)})}
                      >
                        {accountLabel(g.account)}
                      </span>{' '}
                      · {g.nums.length} מספרים
                    </td>
                    <td colSpan={4} className='text-end'>
                      <button className='btn btn-sm btn-success' onClick={() => setSetupAccount(g.account as SetupAccount)}>
                        ⚙️ חתימות ומודעות
                      </button>
                    </td>
                  </tr>
                  {g.nums.map((vn) => numberRow(vn))}
                </Fragment>
              ))}

              {numGroups.free.length > 0 && (
                <>
                  <tr className='table-active'>
                    <td colSpan={2} className='fw-bold'>📞 מספרים פנויים · {numGroups.free.length}</td>
                    <td colSpan={12} className='text-end'>
                      <span className='fs-8 text-muted me-2'>שייך {selectedFree.size} נבחרים ללקוח:</span>
                      <select className='form-select form-select-sm d-inline-block' style={{width: 200}} value={batchTarget} onChange={(e) => setBatchTarget(e.target.value)}>
                        <option value=''>— בחר לקוח —</option>
                        {numGroups.customers.map((g: CustGroup) => (
                          <option key={g.account._id} value={g.account._id}>{accountLabel(g.account)}</option>
                        ))}
                      </select>
                      <button className='btn btn-sm btn-success ms-2' onClick={assignSelected} disabled={!batchTarget || selectedFree.size === 0 || batchBusy}>
                        {batchBusy ? '...' : 'שייך נבחרים'}
                      </button>
                    </td>
                  </tr>
                  {numGroups.free.map((vn: VirtualNumber) => numberRow(vn, true))}
                </>
              )}

              {numbers.length === 0 && (
                <tr>
                  <td colSpan={14} className='text-center text-muted py-6'>
                    אין מספרים. לחץ "סנכרן מ-PayCall".
                  </td>
                </tr>
              )}

              {numbers.length > 0 && filtered.length === 0 && (
                <tr>
                  <td colSpan={14} className='text-center text-muted py-6'>
                    לא נמצאו מספרים עבור "{query}".
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {assignDdi !== null && (
        <div
          className='modal-backdrop-custom'
          style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050}}
          onClick={closeAssign}
        >
          <div className='card' style={{width: 460, maxWidth: '92vw'}} onClick={(e) => e.stopPropagation()}>
            <div className='card-header'>
              <h4 className='card-title m-0'>שיוך מספר {assignDdi}</h4>
            </div>
            <div className='card-body'>
              <label className='form-label'>לקוח</label>
              {selectedAcc ? (
                <div className='d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded'>
                  <span>{accLabel(selectedAcc)}</span>
                  <button className='btn btn-sm btn-light' onClick={() => setSelectedAcc(null)}>
                    שנה
                  </button>
                </div>
              ) : (
                <>
                  <input
                    className='form-control mb-2'
                    placeholder='חיפוש לקוח (שם / אימייל)…'
                    value={accQuery}
                    onChange={(e) => setAccQuery(e.target.value)}
                    autoFocus
                  />
                  {accResults.length > 0 && (
                    <div className='border rounded mb-3' style={{maxHeight: 180, overflowY: 'auto'}}>
                      {accResults.map((a) => (
                        <div
                          key={a._id}
                          className='p-2 border-bottom cursor-pointer hover-bg-light'
                          style={{cursor: 'pointer'}}
                          onClick={() => {
                            setSelectedAcc(a)
                            setAccResults([])
                            // Default the forward/redirect number to the account's own phone (local 05x)
                            if (!forwardNumber.trim()) setForwardNumber(toLocalIsraeliPhone(a.phone || ''))
                          }}
                        >
                          <div className='fw-bold'>{accLabel(a)}</div>
                          {a.email && <div className='text-muted fs-7'>{a.email}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              <label className='form-label'>מספר הפניה (Redirect)</label>
              <input
                className='form-control mb-3'
                dir='ltr'
                placeholder='0541234567'
                value={forwardNumber}
                onChange={(e) => setForwardNumber(e.target.value)}
                onBlur={(e) => setForwardNumber(toLocalIsraeliPhone(e.target.value))}
              />

              {pushEnabled && (
                <>
                  <label className='form-label'>קבוצה ב-PayCall</label>
                  <select
                    className='form-select mb-3'
                    value={groupId}
                    onChange={(e) => setGroupId(e.target.value)}
                  >
                    <option value=''>בחר קבוצה…</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name} ({g.id})
                      </option>
                    ))}
                  </select>
                </>
              )}

              <div className='form-check form-switch mb-2'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='vn-enabled'
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                <label className='form-check-label' htmlFor='vn-enabled'>
                  הפעל מעקב שיחות עבור הלקוח
                </label>
              </div>
            </div>
            <div className='card-footer d-flex justify-content-end gap-2'>
              <button className='btn btn-light' onClick={closeAssign} disabled={saving}>
                ביטול
              </button>
              <button className='btn btn-primary' onClick={onAssign} disabled={saving || !selectedAcc}>
                {saving ? 'שומר…' : 'שמור'}
              </button>
            </div>
          </div>
        </div>
      )}

      {setupAccount && <SignatureSetupModal account={setupAccount} onClose={() => setSetupAccount(null)} />}
      {leadsAccount && <LeadsModal account={leadsAccount} onClose={() => setLeadsAccount(null)} />}
    </div>
  )
}
